import { COIN_SYMBOL_IDS, COLLECT_SYMBOL_ID } from "../../SymbolData";
import {
  goldenMysteryScript,
  verifyGoldenScript,
  goldenCellLimit,
  goldenFinalBoard,
} from "./goldenMysteryStaging";
import { fireMultiplierTrails } from "./goldenMysteryTrails";
import { fireCollectBolts } from "../../Engine/Effects/fireCollectBolts";
import TickerTimeout from "../../utils/TickerTimeout";
// The loop's trace, and the default channel for it. Not temporary: this feature
// has one shot per ~136 spins and every question about it so far has been
// answered by these lines rather than by reading. Silent behind
// FEATURES_CONFIG.DEV_TOOLS.FS_DIAGNOSTICS. See the `diag` parameter, which the
// bonus room swaps for fsDiag so its lines land in the room's own trace.
import { baseDiag } from "../../utils/fsDiag";
import {
  buildGoldenMysteryConfig,
  multiplierSkinFor,
} from "../Main/Mystery/goldenMysteryConfig";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * Plays the Golden Mystery's multiplier and collect loop.
 *
 * ── What it is, and what it is not ──
 *
 * `goldenMysteryScript` has already done the arithmetic: every board state, the
 * multiplied values, what each collector is worth. This module does no maths at
 * all. It walks that script and asks cells to show what the script says they now
 * are — which is why the numbers on screen cannot disagree with the payout.
 *
 * ── The sequence, per iteration ──
 *
 *   1. the drops land          (iteration 0 lands as the reveal's cascade; every
 *                               later one lands as a respin of cleared cells)
 *   2. multipliers FIRE        `mul.distribute` on each AND a trail from every
 *                               multiplier to every coin and collector it
 *                               multiplies, and on arrival an `upgrade` burst
 *                               with the new value landing on that burst's own
 *                               `upgrade` EVENT
 *   3. the collect COLLECTS    bounce -> activation -> hold, alone, with the
 *                               board still. The number pops in mid-activation
 *                               from the collector's own slot
 *   4. cleared cells respin    with the next iteration's drops
 *
 * ── Why it can never block the game ──
 *
 * The feature holds `criticalAnimationPlaying`, so a stall here costs the
 * session and not the round. Every step is therefore skippable: a missing spine
 * plays nothing but still applies the state change, an empty step is a no-op,
 * and the whole run is wrapped by the caller's try/finally. The board follows
 * the script whether or not the animation managed to show it.
 */

const COIN_SYMBOL_ID = COIN_SYMBOL_IDS[0];

/** A beat after a phase, so the eye can see what changed before the next move. */
const PHASE_PAUSE_MS = 260;

/**
 * The beat after the LEVEL phase, before the win is handed over.
 *
 * A HAIR longer than the other phases, and no more: 100ms over the ordinary
 * beat. The bursts are already awaited to their own end and the `upgrade` clip
 * puts the new number on at t=0.267 of 0.4s, so the board is settled by the
 * time this starts — all it buys is a moment between the last coin changing
 * value and the counter taking the screen.
 *
 * It was 900 for one build, which is a wait rather than a beat. Reading time is
 * measured in a fraction of a second here; the round has already been long.
 */
const LEVEL_SETTLE_MS = PHASE_PAUSE_MS + 100;

/**
 * A pause on the TICKER, not on the wall clock.
 *
 * `setTimeout` keeps counting while the tab is hidden, and the animation does
 * not: AppActivityContext sets `PIXI.Ticker.shared.speed = 0` on
 * `visibilitychange`, so every spine and every strip freezes where it stands. A
 * wall-clock pause therefore runs the feature forward over a frozen board, and
 * the player comes back to phases that already happened.
 *
 * TickerTimeout freezes with everything else and resumes where it was. It is
 * what the rest of the game uses, and what the cell controllers' own `_wait`
 * already does.
 */
const wait = (ms) =>
  new Promise((resolve) => {
    new TickerTimeout(resolve, ms);
  });

/**
 * @param {Object} p
 * @param {object} p.round
 * @param {Function} p.cellAt  (reel, row) => GoldenMysteryCellController | null
 * @param {object} [p.trailAnimator] GameView's LevelChargingAnimator, for the
 *   bolts a multiplier sends to the coins it multiplies. Absent means no trails
 *   are drawn and every upgrade still lands.
 * @param {number} [p.levelMultiplier] The bonus level's multiplier. x1 — the
 *   base game, and the bonus at its entry rung — skips the level phase entirely.
 * @param {Function|object} [p.levelOrigin] Where that phase fires FROM: the
 *   centre of the level box, in GLOBAL space. A function is called at fire time,
 *   because the box moves with a resize.
 * @param {Function} [p.onScatterDrop] Called with the scatter cell an iteration
 *   dropped or re-listed, so the game can open its window or turn its strip
 *   again. Absent means neither is presented. See
 *   RevealSymbolController.respin and OverlayReelController.playRevealSequence.
 * @param {Function} [p.diag] Which trace this run belongs in. The loop is shared
 *   by both games and logged as `[BASE]` from either one, which reads as a base
 *   round happening inside the bonus. Free spins passes `fsDiag` and its lines
 *   land in the `[FS]` trace beside the room's own. Same flag for both.
 * @returns {Promise<void>} resolves when the last iteration has settled.
 */
export const runGoldenMysteryLoop = async ({
  round,
  cellAt,
  trailAnimator = null,
  levelMultiplier = 1,
  levelOrigin = null,
  onScatterDrop = null,
  diag = baseDiag,
}) => {
  // Clamped to what this spin actually paid, so a capped round's collector can
  // never show a number bigger than the player gets — see goldenCellLimit.
  const { steps } = goldenMysteryScript(round, {
    cellLimit: goldenCellLimit(round, levelMultiplier),
  });

  // ── One iteration is the COMMON case, and it is not an early exit ──
  //
  // Coins dropped and nothing chained: there is no respin to play, because the
  // reveal's own cascade already showed the board. This used to return right
  // here — and that skipped the level phase below, which is not part of the
  // chain at all. It belongs to every golden round that pays, chained or not,
  // so the loop is what is skipped now, not the rest of the function.
  const chained = steps.length > 1;

  // ── The respins keep the landing's rhythm ──
  //
  // Read from the same config the first landing uses rather than kept here, so
  // there is ONE set of numbers for the feature. They were two arbitrary
  // constants in this file before, and the result was a respin a third the
  // length of the landing with gaps a third the size — the loop looked like a
  // different feature from the reveal that started it.
  const { respinDurationMs, stopStaggerMs } = buildGoldenMysteryConfig();

  const keyOf = (reel, row) => `${reel},${row}`;
  const cellOf = (key) => {
    const [reel, row] = key.split(",").map(Number);
    return cellAt?.(reel, row) ?? null;
  };

  if (chained) {
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];

      // ── 2. The multipliers fire ──
      //
      // All of them together: they do not multiply each other, so there is no
      // order between them and showing them one at a time would only be slower.
      if (step.multipliers.length) {
        // ── STARTED, not awaited ──
        //
        // The box's `distribute` and the trails it throws are ONE gesture, and
        // awaiting the clip first made them two: the box finished its whole
        // performance, and only then did anything leave it. Read as a pause with
        // a delivery afterwards rather than as a throw.
        //
        // Launched here and awaited at the bottom of the phase, so the trails
        // below leave on the same frame the clip begins. The clip is 667ms; the
        // trail and the burst it triggers are 200 + 400. They now overlap
        // instead of queueing, which is the whole 600ms.
        //
        // Safe to fire the trails against a spine that has only just been asked
        // for: a trail starts from the cell's GRID CENTRE, which is geometry and
        // does not wait for a skeleton to exist.
        const distributing = step.multipliers.map(({ key, value }) => {
          const cell = cellOf(key);
          if (!cell) return Promise.resolve();
          return cell.playCellSpine({
            spineName: "mul",
            clip: "distribute",
            skin: multiplierSkinFor(value),
            // The live spine IS the symbol here, so the flat one steps aside.
            hideStrip: true,
          });
        });

        // ── The box HITS the coins ──
        //
        // Everything the multiplier touched bursts and takes its new value — but
        // not until it has been reached. A trail leaves each multiplier for each
        // cell it multiplies, all of them together, and the burst on a cell is
        // the arrival of its own trail.
        //
        // Without this the box did something in its corner and then, a beat
        // later, unrelated cells across the board showed bigger numbers. Nothing
        // said the one caused the other. The trail is that sentence.
        const upgraded = [...step.afterMultiplier.entries()].filter(
          ([, c]) =>
            c.symbolId === COIN_SYMBOL_ID || c.symbolId === COLLECT_SYMBOL_ID,
        );
        const targets = upgraded
          .map(([key, cell]) => [key, cellOf(key), cell])
          .filter(([, controller]) => !!controller);

        const sources = step.multipliers
          .map(({ key }) => cellOf(key))
          .filter(Boolean);

        // The bursts are collected as they are triggered so the phase can wait
        // for all of them — a trail's onArrival cannot be awaited from inside the
        // volley, and the collect must not start over a coin still bursting.
        const bursts = [];
        const burst = (key) => {
          const found = targets.find(([k]) => k === key);
          if (!found) return;
          const [, controller, cell] = found;
          // One burst sound per FRAME, not per coin. A multiplier can upgrade
          // the whole board and the trails land in waves, so several arrivals
          // share a frame — those are one event and must sound like one. The
          // waves themselves are staggered, so each still gets its own.
          soundManager.playOncePerFrame(SOUND_NAME.upgrade);
          bursts.push(
            controller.playCellSpine({
              spineName: "upgrade",
              clip: "upgrade",
              // The value lands on the burst's own `upgrade` event — the frame
              // the animator chose — so the number and the flash can never drift
              // apart.
              onEventName: "upgrade",
              onEvent: () => controller.setItem(cell),
            }),
          );
        };

        await fireMultiplierTrails({
          animator: trailAnimator,
          sources,
          targets: targets.map(([key, controller]) => [key, controller]),
          onArrival: burst,
          diag,
        });
        await Promise.all(bursts);
        // Last, and usually already finished — the clip started 600ms ago and
        // the trail and burst have been running inside it. Awaited rather than
        // dropped so a longer `distribute` is never cut off mid-throw.
        await Promise.all(distributing);
        await wait(PHASE_PAUSE_MS);
      }

      // ── 3. The collect collects, AND NOTHING ELSE MOVES ──
      //
      // Awaited alone, between the multipliers above and the respins below. The
      // collector's own sequence is bounce -> activation -> hold, the number pops
      // in partway through the activation, and lightning reaches out to the coins
      // it is taking. All of that is one statement, and a respin starting
      // underneath it turns the statement into noise.
      //
      // So: nothing before it that has not finished, nothing after it that has not
      // waited. The board is still, the collector acts, then the board moves again.
      if (step.collect) {
        const collector = cellOf(step.collect.key);

        // ── THE COINS FIRE INTO THE MAGNET ──
        //
        // A collector has no clip of its own, so the total used to appear on it
        // with nothing said about where it came from: numbers left the coins and
        // arrived on the magnet, and the two events had no line between them.
        //
        // So every coin being taken shoots the Zeus bolt at it — the exact
        // three-phase strike a Zeus coin fires at its pot, pointed the other way
        // and without the energy balls. See fireCollectBolts.
        //
        // ── The order, and why each piece waits for the one before it ──
        //
        //   bounce            the magnet arrives. Nothing else happens over a
        //                     landing clip; it has not done anything yet.
        //   activation STARTS the coins fire. This is the magnet WORKING, so it
        //                     is the only honest cue — and it comes from inside
        //                     the clip chain (onClipStart), because the two clips
        //                     run on one track and anything awaiting the sequence
        //                     has already missed the handover.
        //   the bolts LAND    the total appears on the magnet, on that frame —
        //                     drawn OVER the live spine, so `activation` is not
        //                     cut short. Ending the clip on impact made the
        //                     magnet stop dead and read as a fault.
        //   each impact       that coin blanks — so what empties it is visibly
        //                     the strike that left it.
        const boltLayer = trailAnimator?.trailContainer ?? null;
        const collectorPoint = collector?.gridCenter?.(boltLayer);
        let bolting = Promise.resolve();

        if (collector) {
          await collector.playCollect(step.collect.total, {
            onActivation: () => {
              if (!boltLayer || !collectorPoint) return;
              bolting = fireCollectBolts({
                layer: boltLayer,
                to: collectorPoint,
                coins: step.collect.swallowed
                  .map((key) => {
                    const cell = cellOf(key);
                    const point = cell?.gridCenter?.(boltLayer);
                    if (!point) return null;
                    return {
                      point,
                      onImpact: () =>
                        cell.setItem?.({ symbolId: COIN_SYMBOL_ID, value: 0 }),
                    };
                  })
                  .filter(Boolean),
                // Live, so a resize mid-flight is survivable — the bolt re-reads
                // this every frame.
                getDimensions: () => ({
                  symbolWidth: trailAnimator.symbolWidth,
                  symbolHeight: trailAnimator.symbolHeight,
                }),
              }).then(() => {
                // ── The number lands with the LAST of the lightning ──
                //
                // Not on arrival. A bolt's `onImpact` fires at the START of its
                // impact phase and the strike then shrinks into the magnet for
                // another 120ms, so a total shown there appeared underneath
                // lightning that was still going — early, by exactly that much.
                //
                // The volley's promise settles when every bolt has finished, so
                // the number arrives as the last of them clears. There is room:
                // charge 120 + travel 210 + impact 120 = 450ms, inside an 833ms
                // `activation`, so the clip is still running and the number is
                // drawn over the live spine rather than after it.
                collector.showCollectedTotal(step.collect.total);
              });
            },
          });
        }

        // The clip and the strikes both run to their own end, and this waits for
        // whichever finishes last.
        await bolting;

        // ── The coins are emptied the moment the magnet has them ──
        //
        // All three sources say the coins are REMOVED — GDD I6, GDD §7's loop and
        // the BE doc — and round 1431382 proves it, refilling the collected cell
        // in the next iteration. But there is no clip for a coin leaving: `c`
        // carries `bounce` and `static` and nothing else.
        //
        // So they are emptied rather than removed. A coin with no value prints no
        // number (see renderCell), so what is left standing is a blank coin
        // waiting to respin — which is true, and is the half of "removed" we can
        // actually draw.
        //
        // The bolts above empty each coin as they land, so this is the backstop
        // for a coin that had no bolt to fire — no layer, no geometry, no
        // particle bundle. Setting a blank coin blank again is a no-op.
        for (const key of step.collect.swallowed) {
          cellOf(key)?.setItem?.({ symbolId: COIN_SYMBOL_ID, value: 0 });
        }

        await wait(PHASE_PAUSE_MS);
      }

      // ── 4. The cleared cells respin, with the next iteration's drops ──
      const next = steps[i + 1];
      if (!next) break;

      const dropsByKey = new Map(
        next.drops.map((d) => [keyOf(d.reel, d.row), d]),
      );
      // Board order, so a row of respins reads as a wave rather than a flicker —
      // the same rule the first landing follows.
      const respinKeys = [...new Set(step.cleared)].sort((a, b) => {
        const [ar, arow] = a.split(",").map(Number);
        const [br, brow] = b.split(",").map(Number);
        return ar - br || arow - brow;
      });

      // ── The scatter opens THE MOMENT ITS OWN CELL LANDS ──
      //
      // Not when the wave finishes. Its cell is one of the respinning ones, and
      // the others are still turning around it — that is the whole shape the
      // first landing already has, where each cell starts its own reveal as it
      // lands. Vladimir: *"it has to open immediately upon landing and spin
      // together with the rest of the coins that are spinning."*
      //
      // Held here so the phase can WAIT for it: *"when those coins finish and
      // if they finish before it, then we wait for golden scatter, then we add
      // multiplier."* So the multiplier and the level phase are both behind it.
      // ── ALL of them, not the first ──
      //
      // One iteration can drop several scatters: a collector empties a handful
      // of cells and more than one of them can refill with `+FS`. Each gets its
      // own window, its own place in the wave and its own promise, and the
      // phase below waits for every one. Vladimir, 2026-09-22 — before this the
      // staging named only the first and the rest stayed shut.
      const scatterSpins = [];
      const scatterDrops = next.scatterDrops || [];
      const scatterKeys = scatterDrops.map((drop) => keyOf(drop.reel, drop.row));
      const openScatter = (drop) => {
        if (!drop || !onScatterDrop) return;

        // ── Its place in the cascade ──
        //
        // The cell is not in `cleared` — nothing empties a scatter — so it has
        // no index of its own in the wave. It takes the one its BOARD POSITION
        // would give it: the number of respinning cells that stop before it.
        // They all start together and finish together, and the scatter is one
        // of them rather than a separate thing happening nearby.
        const stagger = respinKeys.filter((key) => {
          const [r, w] = key.split(",").map(Number);
          return (
            r < drop.reel || (r === drop.reel && w < drop.row)
          );
        }).length;

        const spin = {
          holdMs: respinDurationMs + stagger * stopStaggerMs,
          // Its place in the wave, for the skip registry. Without it the wait
          // registers at 0, sits at the front of the queue and is released with
          // the FIRST coin however long it was told to spin for.
          stopOrder: stagger,
        };

        scatterSpins.push(Promise.resolve(onScatterDrop(drop, spin)));
      };


      // ── Launched FIRST, so the wave leaves together ──
      //
      // `.map` runs each of these synchronously up to its first await, and
      // `respin` calls `strip.start()` before any of them — so every coin is
      // already turning by the time this array exists.
      const wave = respinKeys.map(async (key, index) => {
          const controller = cellOf(key);
          const outcome = dropsByKey.get(key);
          // A cleared cell with nothing queued for it should not happen — the
          // server refills what it empties — but if it does, leaving the cell as
          // it is beats spinning it forever.
          if (!controller || !outcome) return;
          await controller.respin(
            outcome,
            respinDurationMs + index * stopStaggerMs,
            // Same index the stagger uses, so a slam stop walks this wave in
            // the order it was already going to land in.
            index,
          );
          // THIS cell just landed on the scatter. Started here, inside the
          // wave, and deliberately not awaited — the rest of the wave carries
          // on around it.
          const landed = scatterDrops[scatterKeys.indexOf(key)];
          if (landed) openScatter(landed);
        });

      // ── ...and the scatter goes with them, not before them ──
      //
      // A cell that is NOT respinning — a blank SC1 re-rolling behind a window
      // that never moved — has nothing to land first, so it starts with the
      // wave. AFTER the array above, because started before it the scatter had
      // a head start on every coin and visibly pulled away from them.
      // Vladimir, 2026-09-22: *"starts earlier this is our bug, they started at
      // same time before."*
      scatterDrops.forEach((drop, i) => {
        if (!respinKeys.includes(scatterKeys[i])) openScatter(drop);
      });

      await Promise.all(wave);

      // The coins have all landed. If the scatter is still deciding, the board
      // waits for it before anything else moves — the next iteration's
      // multipliers, and the level phase after the loop, are both downstream of
      // this line.
      if (scatterSpins.length) {
        await Promise.all(scatterSpins);
      }
    }
  }

  // ── 5. The level box multiplies the whole board — bonus game only ──
  //
  // GDD §7: "in bonus game: apply the Level multiplier FIRST", before the sum is
  // paid. The server has already done the arithmetic — capture 1431382 paid
  // sum 17 x bet 10 x the level-3 x3 — so the board is holding numbers that are
  // short by exactly the level, and the counter is about to pay the full amount.
  // This phase is what puts the two back in agreement, and it is the last thing
  // that happens before the win is presented.
  //
  // It reads as the box CLAIMING the board: one volley from the centre of the
  // level box on the right, every coin and every collector hit on the same
  // frame, each taking its new number on its own upgrade burst. The same volley
  // a multiplier fires, from a box instead of a cell — see fireMultiplierTrails.
  //
  // Simultaneous on purpose. The level multiplies the whole payout at once; hit
  // one at a time it would read as a second collection round.
  const level = levelMultiplier || 1;
  if (level > 1 && steps.length) {
    const origin =
      typeof levelOrigin === "function" ? levelOrigin() : levelOrigin;

    // The money cells as the loop LEFT them, raw. Anything with no value is not
    // paid and is not hit — an emptied coin, a spent multiplier's cell.
    const money = [...goldenFinalBoard(round).entries()].filter(
      ([, c]) =>
        (c.symbolId === COIN_SYMBOL_ID || c.symbolId === COLLECT_SYMBOL_ID) &&
        (c.value || 0) > 0,
    );
    const targets = money
      .map(([key, cell]) => [key, cellOf(key), cell])
      .filter(([, controller]) => !!controller);

    if (targets.length) {
      const bursts = [];
      const burst = (key) => {
        const found = targets.find(([k]) => k === key);
        if (!found) return;
        const [, controller, cell] = found;
        // Same burst, same gate — see the multiplier volley above.
        soundManager.playOncePerFrame(SOUND_NAME.upgrade);
        bursts.push(
          controller.playCellSpine({
            spineName: "upgrade",
            clip: "upgrade",
            onEventName: "upgrade",
            onEvent: () =>
              controller.setItem({
                ...cell,
                value: (cell.value || 0) * level,
              }),
          }),
        );
      };

      await fireMultiplierTrails({
        animator: trailAnimator,
        origins: origin ? [origin] : [],
        targets: targets.map(([key, controller]) => [key, controller]),
        onArrival: burst,
        diag,
      });
      // The win must not be presented over a coin still bursting — this phase
      // is the last thing between the feature and the counter.
      await Promise.all(bursts);
      // Then a beat to READ it — see LEVEL_SETTLE_MS.
      await wait(LEVEL_SETTLE_MS);
    }
  }

  // The board should now hold exactly what the payout was calculated from. This
  // says so out loud when it does not — see verifyGoldenScript.
  const check = verifyGoldenScript(round, level);
  if (check) {
    diag("golden payout check", {
      ok: check.ok,
      expected: check.expected,
      actual: check.actual,
      level,
    });
  }
};

export default runGoldenMysteryLoop;
